import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  inject,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { PlatformService } from '@examdojo/platform';
import { TranslocoPipe } from '@jsverse/transloco';
import { FilterSectionData, FilterSectionDirective } from './filter-section.directive';
import { FiltersPanelDialogService } from './filters-panel-dialog.service';

@Component({
  selector: 'dojo-multiple-filters-button',
  templateUrl: './multiple-filters-button.component.html',
  styleUrl: './multiple-filters-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatMenuModule, NgClass, NgTemplateOutlet, TranslocoPipe, IconComponent, ButtonComponent],
})
export class MultipleFiltersButtonComponent {
  private readonly platformService = inject(PlatformService);
  private readonly dialogService = inject(FiltersPanelDialogService);

  readonly sections = contentChildren(FilterSectionDirective);

  readonly buttonLabel = input.required<string>();
  readonly titleLabel = input<string>();

  readonly clearAll = output<void>();

  readonly menuTrigger = viewChild<MatMenuTrigger>('menuTrigger');

  readonly selectedSectionName = linkedSignal<string>(() => {
    const sections = this.sections();
    if (sections.length > 0) {
      return sections[0].name();
    }
    return '';
  });

  readonly state = connectState({
    isSmall: this.platformService.isSmall$,
  });

  readonly totalCount = computed(() => this.sections().filter((s) => s.count() > 0).length);

  readonly isSelected = computed(() => this.totalCount() > 0);

  readonly selectedSection = computed(() => this.sections().find((s) => s.name() === this.selectedSectionName()));

  readonly filtersData = computed<FilterSectionData[]>(() =>
    this.sections().map((section) => ({
      name: section.name(),
      label: section.label(),
      count: section.count(),
      template: section.templateRef,
      clearFn: section.clearFn(),
    })),
  );

  onButtonClick(): void {
    if (this.state.isSmall) {
      this.openMobileDialog();
    }
  }

  selectSection(name: string): void {
    this.selectedSectionName.set(name);
  }

  onClearAll(): void {
    this.clearAll.emit();
  }

  closeMenu(): void {
    this.menuTrigger()?.closeMenu();
  }

  private openMobileDialog(): void {
    this.dialogService.open({
      titleLabel: this.titleLabel() ?? this.buttonLabel(),
      filters: this.filtersData(),
      isSelected: this.isSelected,
      clearAllFn: () => this.clearAll.emit(),
    });
  }
}
